/**
 * Cross-job duplicate non-compliance check for PVD/BTY combo visits.
 *
 * CER asked that the same finding is not recorded on both halves of a combo
 * site visit. Clause IDs differ across PV vs battery books, so "same" is
 * primarily normalised recommendation text (plus an exact clauseId match when
 * both sides happen to share one). This is a WARNING only — never a submit
 * block and never an automatic dedupe.
 */
import { assessmentRequiresWording, assessmentValueForClauseResponse, } from './assessment-library.js';
/** Fold whitespace and case so paraphrased-identical canned wording still matches. */
export function normalizeRecommendationText(text) {
    return text.replace(/\s+/g, ' ').trim().toLowerCase();
}
export function isNonComplianceFinding(row) {
    const assessment = assessmentValueForClauseResponse(row);
    if (assessmentRequiresWording(assessment))
        return true;
    return row.outcome === 'Non-Compliant';
}
export function nonComplianceFindings(checklist) {
    const out = [];
    for (const row of checklist) {
        if (!isNonComplianceFinding(row))
            continue;
        const recommendation = (row.recommendation ?? '').trim();
        if (!recommendation)
            continue;
        out.push({
            section: row.section,
            clauseId: row.clauseId,
            recommendation,
            ...(row.assessment !== undefined ? { assessment: row.assessment } : {}),
            ...(row.outcome !== undefined ? { outcome: row.outcome } : {}),
        });
    }
    return out;
}
/**
 * Pair local findings with partner findings that share normalised wording
 * (or the same clauseId). One local row may match several partner rows; we
 * do not collapse or dedupe — the caller shows every hit.
 */
export function duplicateNonComplianceMatches(localChecklist, partnerChecklist) {
    const local = nonComplianceFindings(localChecklist);
    const partner = nonComplianceFindings(partnerChecklist);
    const matches = [];
    for (const left of local) {
        const leftNorm = normalizeRecommendationText(left.recommendation);
        for (const right of partner) {
            if (left.clauseId === right.clauseId) {
                matches.push({
                    recommendation: left.recommendation,
                    local: left,
                    partner: right,
                    reason: 'clauseId',
                });
                continue;
            }
            if (leftNorm.length > 0 && leftNorm === normalizeRecommendationText(right.recommendation)) {
                matches.push({
                    recommendation: left.recommendation,
                    local: left,
                    partner: right,
                    reason: 'recommendation',
                });
            }
        }
    }
    return matches;
}
export function formatDuplicateNonComplianceMatch(match) {
    if (match.reason === 'clauseId') {
        return `${match.local.clauseId}: same clause as partner (${match.partner.clauseId}) — "${match.recommendation}"`;
    }
    return `${match.local.clauseId} ↔ ${match.partner.clauseId}: same wording — "${match.recommendation}"`;
}
export function formatPartnerNonComplianceList(partnerJobId, partnerChecklist) {
    return nonComplianceFindings(partnerChecklist).map((f) => `${partnerJobId} ${f.clauseId}: ${f.recommendation}`);
}
//# sourceMappingURL=duplicate-noncompliance.js.map